import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, ShieldAlert, Copy, Check, Trash2 } from 'lucide-react';
import type { Factor } from '@supabase/supabase-js';

interface EnrollState {
  factorId: string;
  qrCode: string;
  secret: string;
}

interface MfaEnrollmentProps {
  /**
   * Called after a TOTP factor is successfully verified. Used by the internal
   * admin gate to invalidate its ['mfa-factors'] query so the gate lifts
   * immediately without a reload.
   */
  onEnrolled?: () => void;
}

/** A TOTP QR code comes back as an SVG. Newer supabase-js returns a full
 * data-URI; older shapes return raw SVG markup — handle both. */
function qrSrc(qr: string): string {
  return qr.startsWith('data:') ? qr : `data:image/svg+xml;utf-8,${encodeURIComponent(qr)}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Self-contained two-factor-authentication management card.
 *
 * - No verified TOTP factor → "Enable two-factor authentication" CTA that runs
 *   the enroll → scan QR → verify 6-digit code flow.
 * - A verified factor exists → shows its name/date + a confirm-gated "Remove".
 *
 * Stale unverified factors (left behind by an abandoned enrollment) are cleaned
 * up before starting a fresh enrollment so the account never accumulates them.
 */
export function MfaEnrollment({ onEnrolled }: MfaEnrollmentProps) {
  const [factors, setFactors] = useState<Factor[] | null>(null); // null = loading
  const [enroll, setEnroll] = useState<EnrollState | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false); // enroll-start / verify in flight
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFactors = useCallback(async () => {
    const { data, error: listErr } = await supabase.auth.mfa.listFactors();
    if (listErr) {
      setFactors([]);
      return;
    }
    setFactors(data?.totp ?? []);
  }, []);

  useEffect(() => {
    void loadFactors();
  }, [loadFactors]);

  const startEnroll = async () => {
    setBusy(true);
    setError(null);
    try {
      // Clear any stale unverified TOTP factors first — Supabase rejects a new
      // enroll while a same-named unverified factor lingers.
      const { data: existing } = await supabase.auth.mfa.listFactors();
      const stale = (existing?.all ?? []).filter(
        (f) => f.factor_type === 'totp' && f.status === 'unverified',
      );
      for (const f of stale) {
        await supabase.auth.mfa.unenroll({ factorId: f.id });
      }

      // Pass an explicit brand issuer so authenticator apps label the entry
      // "IDSeat". Without it, Supabase Auth defaults the otpauth issuer to the
      // project's Site URL host — which surfaced as "localhost:3000".
      const { data, error: enrollErr } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        issuer: 'IDSeat',
      });
      if (enrollErr || !data) throw enrollErr ?? new Error('Enrollment failed');
      setEnroll({ factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret });
      setCode('');
    } catch {
      toast.error('Could not start two-factor setup — please try again');
    } finally {
      setBusy(false);
    }
  };

  const cancelEnroll = async () => {
    if (enroll) {
      // Drop the just-created unverified factor so it doesn't linger.
      await supabase.auth.mfa.unenroll({ factorId: enroll.factorId }).catch(() => {});
    }
    setEnroll(null);
    setCode('');
    setError(null);
  };

  const verifyCode = async () => {
    if (!enroll || code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({
        factorId: enroll.factorId,
      });
      if (chErr || !challenge) throw chErr ?? new Error('Challenge failed');

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId: enroll.factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyErr) throw verifyErr;

      toast.success('Two-factor authentication is now enabled');
      setEnroll(null);
      setCode('');
      await loadFactors();
      onEnrolled?.();
    } catch {
      setError('That code did not match. Check your authenticator app and try again.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const removeFactor = async (factorId: string) => {
    setRemovingId(factorId);
    try {
      const { error: unErr } = await supabase.auth.mfa.unenroll({ factorId });
      if (unErr) throw unErr;
      toast.success('Two-factor authentication removed');
      await loadFactors();
    } catch {
      toast.error('Could not remove two-factor authentication');
    } finally {
      setRemovingId(null);
    }
  };

  const copySecret = async () => {
    if (!enroll) return;
    try {
      await navigator.clipboard.writeText(enroll.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy the code');
    }
  };

  const hasFactor = (factors?.length ?? 0) > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          {hasFactor ? (
            <ShieldCheck className="w-5 h-5 text-primary" />
          ) : (
            <ShieldAlert className="w-5 h-5 text-primary" />
          )}
          <CardTitle>Two-factor authentication</CardTitle>
        </div>
        <CardDescription>
          Add a time-based one-time code from an authenticator app as a second step when signing in.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {factors === null ? (
          <div className="flex items-center gap-2 text-[14px] text-muted-foreground py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : enroll ? (
          /* ── Enrollment in progress: scan QR + verify ── */
          <div className="space-y-4">
            <p className="text-[14px] text-muted-foreground">
              Scan this QR code with your authenticator app (1Password, Google Authenticator, Authy…),
              then enter the 6-digit code it shows.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
              <div className="w-56 h-56 rounded-[11px] border border-border bg-white p-3 flex-shrink-0">
                <img
                  src={qrSrc(enroll.qrCode)}
                  alt="Two-factor authentication QR code"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <Label className="text-[14px]">Can't scan the QR? Add the key manually</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 min-w-0 break-all rounded-[9px] border border-border bg-muted px-3 py-2 text-[13px] font-mono">
                    {enroll.secret}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="rounded-[11px] flex-shrink-0"
                    onClick={copySecret}
                    aria-label="Copy setup code"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-[13px] text-muted-foreground">
                  Copy this key and paste it into your authenticator app (choose “Enter
                  a setup key” / manual entry). That registers IDSeat in the app — you
                  don't type it into the boxes below; the app then shows the 6-digit code.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mfa-code" className="text-[14px]">
                Verification code
              </Label>
              <InputOTP
                id="mfa-code"
                maxLength={6}
                value={code}
                onChange={setCode}
                containerClassName="has-[:disabled]:opacity-50"
                disabled={busy}
              >
                {/* gap-2.5 + per-slot border-l/rounding makes each digit its own
                    spaced box rather than the default joined strip. */}
                <InputOTPGroup className="gap-2.5">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot
                      key={i}
                      index={i}
                      className="w-12 h-12 text-lg rounded-[10px] border-l"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              {error && <p className="text-[13px] text-destructive">{error}</p>}
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                className="rounded-[11px]"
                onClick={verifyCode}
                disabled={busy || code.length !== 6}
              >
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Verify and enable
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="rounded-[11px]"
                onClick={cancelEnroll}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : hasFactor ? (
          /* ── Enrolled: list factors + remove ── */
          <div className="space-y-3">
            {factors!.map((f) => (
              <div
                key={f.id}
                className="flex items-center gap-3 rounded-[11px] border border-border p-3"
              >
                <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-foreground truncate">
                    {f.friendly_name || 'Authenticator app'}
                  </div>
                  <div className="text-[13px] text-muted-foreground">
                    Enabled{f.created_at ? ` · ${formatDate(f.created_at)}` : ''}
                  </div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-[11px] flex-shrink-0"
                      disabled={removingId === f.id}
                    >
                      {removingId === f.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-1.5" />
                          Remove
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove two-factor authentication?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Your account will be protected by your password only. You can re-enable
                        two-factor authentication at any time.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-[11px]">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="rounded-[11px] bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => removeFactor(f.id)}
                      >
                        Remove
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        ) : (
          /* ── Not enrolled: CTA ── */
          <div className="space-y-3">
            <p className="text-[14px] text-muted-foreground">
              Two-factor authentication is not enabled on your account.
            </p>
            <Button type="button" className="rounded-[11px]" onClick={startEnroll} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <ShieldCheck className="w-4 h-4 mr-2" />
              Enable two-factor authentication
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default MfaEnrollment;
